import {
  Button,
  Callout,
  Card,
  FormLayout,
  GridFieldGroup,
  Heading,
  Link,
  LoadingDots,
  MaskedInput,
  Paragraph,
  Select,
  SubmitButton,
  TextInput,
  useDialog,
} from '@expressable/syntax-ds';
import { SealQuestion } from '@phosphor-icons/react';
import {
  DirectSignupAddSecondaryInsuranceDialog,
  DirectSignupChangeInsuranceDialog,
  DirectSignupEditAppointmentDialog,
} from 'components/Dialog';
import config from 'config';
import { InsuranceSubscriberRelationship } from 'domains/client';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { handleCheckEligibility } from 'domains/signupDirect/Evaluation/Insurance/eligibility';
import { getSubscriberRelationshipOptions } from 'domains/signupDirect/misc/constants';
import { JotformRedirectReason } from 'domains/signupDirect/misc/jotform/urls';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { EvaluationFormType, paymentMethodIsInsurance } from 'domains/signupDirect/schemas/evaluationSchema';
import { InsuranceEligibilityCheckCard } from 'domains/signupDirect/SyntaxComps/EligibilityCard';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTrack } from 'utils/hooks/useMixPanel';
import { createLeadOnEligibilityCheck } from './createLeadOnEligibilityCheck';
import { EligibilityPCPInformation } from './EligibilityPCPINformation';

export const DirectSignupInsuranceEligibilitySection = ({
  showUpdateInfo,
  setShowUpdateInfo,
}: {
  showUpdateInfo?: boolean;
  setShowUpdateInfo?: (show: boolean) => void;
}) => {
  const { track, trackAsync } = useTrack();
  const {
    dialogStateProps: addSecondaryInsuranceProps,
    onOpen: onOpenAddSecondaryInsurance,
    onClose: onCloseAddSecondaryInsurance,
  } = useDialog();
  const { dialogStateProps: changeInsuranceProps, onOpen: onOpenChangeInsurance } = useDialog();
  const { dialogStateProps: editAppointmentProps } = useDialog();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormContext<EvaluationFormType>();
  const {
    filters,
    evaluationSchedule,
    onReturnToFilters,
    onContinueToScheduleEval,
    onSaveIncompleteEvalForm,
    resumedLeadState,
    isResumedLead,
    onUpdateEligibilityHasPCP,
    eligibilityHasPCP,
    onContinueToJotformRegistrationForm,
  } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';
  if (!evaluationSchedule) throw 'Expected evaluationSchedule to be defined';

  const shouldSkipEligibility = resumedLeadState?.shouldSkipEligibility;

  const paymentMethod = useWatch<EvaluationFormType, 'billingInfo'>({ name: 'billingInfo' });
  if (!paymentMethodIsInsurance(paymentMethod)) throw 'Expected paymentMethod.type to be insurance';

  const { insuranceData } = paymentMethod;

  if (!insuranceData) throw 'Expected currentInsuranceData to be defined';

  const onSubmitEligibilityCheck = handleSubmit(async data => {
    const checkResult = await handleCheckEligibility({ data, setValue, onUpdateEligibilityHasPCP });
    await createLeadOnEligibilityCheck(data, getValues, setValue, filters, checkResult);
  });

  const checkResult = useWatch<EvaluationFormType, 'billingInfo.eligibilityCheckResult'>({
    name: 'billingInfo.eligibilityCheckResult',
  });

  const subscriberRelationshipOptions = getSubscriberRelationshipOptions(t);

  const handleSubmitInsuranceCard = async () => {
    await trackAsync(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.REDIRECT_TO_JOTFORM.INSURANCE_CARD);

    onContinueToJotformRegistrationForm(JotformRedirectReason.ELIGIBILITY_BAILOUT);
  };

  useEffect(() => {
    if (paymentMethod.insuranceData.insuranceType === 'Medicaid') {
      if (eligibilityHasPCP) {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.FORM.PCP_PREFILLED);
      } else {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.FORM.PCP_NOT_PREFILLED);
      }
    }
  }, [eligibilityHasPCP, paymentMethod.insuranceData.insuranceType]);

  return (
    <>
      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.insurance.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.insurance.subtitle')}
          </Paragraph>
        </HeadingCombo>

        <div className="flex flex-col gap-e16">
          <TextInput
            data-testid="insurance-plan-readonly-input"
            label={t('directSignup.sections.insurance.labels.insurancePlan')}
            {...register('billingInfo.insuranceData.displayName')}
            readOnly
          />

          {!shouldSkipEligibility && (
            <Button data-testid="change-insurance-button" variant="link" size="sm" onClick={onOpenChangeInsurance}>
              {t('directSignup.sections.insurance.changeInsurance')}
            </Button>
          )}
        </div>

        <GridFieldGroup>
          <TextInput
            data-testid="insurance-member-id-input"
            label={t('directSignup.sections.insurance.labels.memberId')}
            readOnly={resumedLeadState?.shouldSkipEligibility}
            hint={t('directSignup.sections.insurance.hint.memberId')}
            {...register('billingInfo.memberId')}
          />

          <TextInput
            data-testid="insurance-group-id-input"
            label={t('directSignup.sections.insurance.labels.groupId')}
            readOnly={resumedLeadState?.shouldSkipEligibility}
            hint={t('directSignup.sections.insurance.hint.groupId')}
            {...register('billingInfo.groupId')}
          />
        </GridFieldGroup>

        {paymentMethod.type === 'insurance-commercial' && (
          <Callout className="w-full" type="info">
            {t('directSignup.sections.insurance.labels.subscriber.verifySubscriberInfoCopy')}
          </Callout>
        )}

        {paymentMethod.type === 'insurance-commercial' && (
          <GridFieldGroup>
            <Select
              label={t('directSignup.sections.insurance.labels.subscriber.relationshipToClient')}
              name="billingInfo.subscriber.relationShipToClient"
              options={subscriberRelationshipOptions}
              readOnly={resumedLeadState?.shouldSkipEligibility}
            />

            {paymentMethod.subscriber?.relationShipToClient !== InsuranceSubscriberRelationship.Self && (
              <>
                <MaskedInput
                  variant="date"
                  name="billingInfo.subscriber.dateOfBirth"
                  data-testid="insurance-subscriber-dob-input"
                  label={t('directSignup.sections.insurance.labels.subscriber.dateOfBirth')}
                  readOnly={resumedLeadState?.shouldSkipEligibility}
                />
                <TextInput
                  label={t('directSignup.sections.insurance.labels.subscriber.firstName')}
                  {...register('billingInfo.subscriber.firstName')}
                  readOnly={resumedLeadState?.shouldSkipEligibility}
                />
                <TextInput
                  label={t('directSignup.sections.insurance.labels.subscriber.lastName')}
                  {...register('billingInfo.subscriber.lastName')}
                  readOnly={resumedLeadState?.shouldSkipEligibility}
                />
              </>
            )}
          </GridFieldGroup>
        )}

        {!shouldSkipEligibility && (
          <Button
            data-testid="add-secondary-insurance-button"
            variant="link"
            className="mb-64"
            onClick={onOpenAddSecondaryInsurance}
          >
            {t('directSignup.sections.insurance.addSecondaryInsurance')}
          </Button>
        )}

        <Heading noDefaultMargins level="h5">
          {t('directSignup.sections.billing')}
        </Heading>

        {isSubmitting &&
        checkResult !== EligibilityCheckResult.IN_NETWORK &&
        checkResult !== EligibilityCheckResult.OUT_OF_NETWORK ? (
          <Card className={`border p-e32 min-h-[313px] flex items-center`}>
            <LoadingDots
              className="bg-transparent static"
              message={t('directSignup.sections.insurance.eligibilityCheck.loading')}
            />
          </Card>
        ) : (
          <>
            {!checkResult && insuranceData.networkStatus === 'In-network' && (
              <Card className={`border p-e32`}>
                <Heading level="h6" className="flex items-center -ml-8" noDefaultMargins>
                  <div className="p-e12 rounded-full bg-gray-100 text-gray-600 mx-8">
                    <SealQuestion weight="fill" size={20} />
                  </div>
                  {t('directSignup.sections.insurance.eligibilityCheck.title')}
                </Heading>
                <SubmitButton
                  data-testid="check-insurance-eligibility-button"
                  variant="secondary"
                  className="my-32"
                  onClick={onSubmitEligibilityCheck}
                >
                  {t('directSignup.sections.insurance.eligibilityCheck.submit')}
                </SubmitButton>

                <Paragraph size="sm" variant="secondary" className="mt-e16" noDefaultMargins>
                  {t('directSignup.eval.form.terms.1')}
                  <Link to={config.get('termsUrl') as string} external>
                    {t('directSignup.eval.form.terms.2')}
                  </Link>
                  {t('directSignup.eval.form.terms.7')}
                  <Link to={config.get('privacyPolicyUrl') as string} external>
                    {t('directSignup.eval.form.terms.8')}
                  </Link>
                  {t('directSignup.eval.form.terms.9')}
                  {t('directSignup.eval.form.terms.10')}
                </Paragraph>
              </Card>
            )}

            <div className="flex flex-col gap-e64">
              {(checkResult || insuranceData.networkStatus === 'Out-of-network') && (
                <InsuranceEligibilityCheckCard
                  showUpdateInfo={showUpdateInfo}
                  setShowUpdateInfo={setShowUpdateInfo}
                  result={checkResult}
                  onSubmitEligibilityCheck={onSubmitEligibilityCheck}
                  isSkippedEligibility={shouldSkipEligibility}
                  onSubmitInsuranceCard={handleSubmitInsuranceCard}
                />
              )}
            </div>

            {eligibilityHasPCP && <EligibilityPCPInformation />}
          </>
        )}
      </FormLayout.Fields>

      <DirectSignupAddSecondaryInsuranceDialog
        {...addSecondaryInsuranceProps}
        onConfirm={() => {
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.ADD_SECONDARY_INSURANCE, { isResumedLead });
          onCloseAddSecondaryInsurance();
        }}
      />
      <DirectSignupChangeInsuranceDialog
        {...changeInsuranceProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onReturnToFilters();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_PAYMENT_METHOD.FROM_INSURANCE, { isResumedLead });
        }}
      />
      <DirectSignupEditAppointmentDialog
        {...editAppointmentProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onContinueToScheduleEval();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_APPOINTMENT, { isResumedLead });
        }}
      />
    </>
  );
};
